/**
 * A marker for the window in which a chooser outside React's tree owns the screen.
 *
 * The Google Drive Picker paints its chooser into `document.body`, a sibling of
 * React's root rather than a descendant of it. Radix decides whether a pointer
 * landed "outside" a dismissable layer by asking whether it landed inside that
 * layer's own React subtree, so every click inside such a chooser reads as an
 * outside click and dismisses whatever dialog or sheet the trigger lives in.
 *
 * On the admin Courses sheet that meant pressing Cancel in the Picker closed the
 * whole course record and destroyed the unsaved material draft the chooser had
 * been opened to fill — found in production on `portal.mufidseminary.org` as
 * acceptance row A17 of #277. Nothing was written, so it was lost typing rather
 * than corruption, but it happened on every cancellation.
 *
 * The deterministic Picker fake could not see it: a fake that returns a value
 * mounts no overlay and dispatches no pointer event, so the cancel path was
 * green in the journey suite throughout.
 *
 * Only the chooser knows when its overlay is up, so it says so here and the
 * layers that would otherwise dismiss ask before they do. Deliberately not React
 * state — the answer is read inside an event handler at the instant of the
 * event, so nothing has to re-render when it changes.
 */

/**
 * The window's own ceiling, above the longest bound any single chooser step has
 * (a 300s Picker plus a 120s token popup).
 *
 * The counter is module-global and every record sheet reads it, so one chooser
 * that never settles would degrade the whole application for the life of the
 * page. Each step is separately bounded, and this exists so that the invariant
 * "the counter always drains" is a property of this file rather than a promise
 * every future caller has to keep.
 */
export const EXTERNAL_OVERLAY_CEILING_MS = 480_000;

let depth = 0;
const listeners = new Set<() => void>();

/** True while a chooser outside React's tree owns the screen. */
export function externalOverlayIsOpen(): boolean {
  return depth > 0;
}

/**
 * Called when the window opens and when it closes, never in between.
 *
 * Dismissal is decided inside an event handler and needs no subscription, but
 * releasing the focus trap has to happen for the *duration* of the window rather
 * than at one instant, so that consumer needs to know when it starts and stops.
 */
export function subscribeToExternalOverlay(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function announce(): void {
  document.body.toggleAttribute("data-external-overlay-open", externalOverlayIsOpen());
  // Copied, so a listener that unsubscribes itself cannot alter the set mid-walk.
  for (const listener of [...listeners]) listener();
}

/**
 * Marks `run` as owning an out-of-tree overlay for the whole of its duration.
 *
 * The window deliberately opens before the overlay is painted and closes after
 * it is torn down. The identity and token popups are out-of-tree surfaces too,
 * and Google's chooser frame is only removed on a deferred teardown, so a window
 * drawn tightly around the visible chooser would have a gap at each end.
 */
export async function withExternalOverlay<T>(run: () => Promise<T>): Promise<T> {
  depth += 1;
  if (depth === 1) announce();
  const ceiling = window.setTimeout(release, EXTERNAL_OVERLAY_CEILING_MS);
  let released = false;

  function release(): void {
    // Idempotent, because the ceiling and the normal path race by design.
    if (released) return;
    released = true;
    window.clearTimeout(ceiling);
    depth -= 1;
    if (depth === 0) announce();
  }

  try {
    return await run();
  } finally {
    // `finally`, not a trailing decrement: a chooser that throws must not leave
    // every sheet in the application undismissable. Counted rather than a
    // boolean so that nesting and concurrency both settle back to zero.
    release();
  }
}
